/**
 * QA & Compliance Dashboard
 * Automated quality scoring, compliance monitoring, review queue, and coaching notes
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Shield, AlertTriangle, CheckCircle, XCircle, Plus, Pencil, Trash2,
  ListChecks, Eye, BookOpen,
} from 'lucide-react';
import MetricCard from '../components/dashboard/MetricCard';
import SentimentPieChart from '../components/dashboard/SentimentPieChart';
import DateRangeFilter from '../components/dashboard/DateRangeFilter';
import Spinner from '../components/shared/Spinner';
import EmptyState from '../components/shared/EmptyState';
import RefreshButton from '../components/shared/RefreshButton';
import ConfirmDialog from '../components/shared/ConfirmDialog';
import AgentName from '../components/shared/AgentName';
import ComplianceRuleModal from '../components/dashboard/ComplianceRuleModal';
import { PermissionGate } from '@shared/auth';
import { StandardPermission } from '@shared/auth/permissions';
import useDateRange from '../hooks/useDateRange';
import useDebounce from '../hooks/useDebounce';
import { toTitleCase, formatShortDate } from '../utils/formatters';
import { onActivateKey } from '../utils/a11y';
import { getQASummary, getSubParameterAnalytics } from '../services/qaService';
import {
  getComplianceSummary,
  getComplianceRules,
  createComplianceRule,
  updateComplianceRule,
  deleteComplianceRule,
} from '../services/complianceService';
import { getReviewQueue, getCoachingNotes } from '../services/coachingService';

const QUALITY_LABELS = {
  empathy: 'Empathy',
  resolution: 'Resolution',
  professionalism: 'Professionalism',
  knowledge: 'Knowledge',
  communication: 'Communication',
  compliance_adherence: 'Compliance',
  overall: 'Overall',
};

const SEVERITY_COLORS = {
  high: 'bg-red-100 text-red-800 border-red-200',
  medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  low: 'bg-blue-100 text-blue-800 border-blue-200',
};

const SEVERITY_DOTS = {
  high: 'bg-red-500',
  medium: 'bg-yellow-500',
  low: 'bg-blue-400',
};

const QUALITY_RATINGS = {
  excellent: 'Excellent',
  good: 'Good',
  average: 'Average',
  below_avg: 'Below Avg',
  needs_work: 'Needs Work',
  poor: 'Poor',
};

const QUALITY_DIST_LABELS = { positive: 'Good & Above', neutral: 'Average', negative: 'Needs Improvement' };
const QUALITY_DIST_COLORS = { positive: '#22c55e', neutral: '#f59e0b', negative: '#ef4444' };

const STATUS_BADGE = {
  pending: 'bg-yellow-100 text-yellow-800',
  in_progress: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  dismissed: 'bg-gray-100 text-gray-600',
};

const DimensionBar = ({ label, value }) => {
  const pct = Math.round((value || 0) * 100);
  const color = pct >= 80 ? 'bg-green-500' : pct >= 60 ? 'bg-yellow-500' : pct >= 40 ? 'bg-orange-500' : 'bg-red-500';
  return (
    <div className="flex items-center space-x-3" title={`${label}: ${pct}% (${value?.toFixed(2) ?? '-'})`}>
      <span className="text-sm text-gray-600 w-28 truncate">{label}</span>
      <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={label}>
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-sm font-medium text-gray-700 w-10 text-right">{value?.toFixed(2)}</span>
    </div>
  );
};

const ScorePill = ({ score }) => {
  if (score === null || score === undefined) return <span className="text-xs text-gray-400">-</span>;
  const pct = Math.round(score * 100);
  const color = pct >= 70 ? 'text-green-700 bg-green-50' : pct >= 40 ? 'text-yellow-700 bg-yellow-50' : 'text-red-700 bg-red-50';
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded ${color}`}>{pct}%</span>
  );
};

const CoachingPage = () => {
  const navigate = useNavigate();
  const { from, to, onChange } = useDateRange();
  const debouncedFrom = useDebounce(from, 400);
  const debouncedTo = useDebounce(to, 400);
  const [loading, setLoading] = useState(true);
  const [qa, setQa] = useState(null);
  const [subParams, setSubParams] = useState([]);
  const [compliance, setCompliance] = useState(null);
  const [complianceRules, setComplianceRules] = useState([]);
  const [reviewQueue, setReviewQueue] = useState([]);
  const [reviewTotal, setReviewTotal] = useState(0);
  const [coachingNotes, setCoachingNotes] = useState([]);
  const [notesTotal, setNotesTotal] = useState(0);

  const [ruleModalOpen, setRuleModalOpen] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const fetchIdRef = useRef(0);

  const fetchData = useCallback(async () => {
    const fetchId = ++fetchIdRef.current;
    try {
      setLoading(true);
      const dateRange = { from: debouncedFrom, to: debouncedTo };
      const [qaRes, subParamsRes, compRes, rulesRes, reviewRes, notesRes] = await Promise.all([
        getQASummary(dateRange),
        getSubParameterAnalytics(dateRange),
        getComplianceSummary(dateRange),
        getComplianceRules(),
        getReviewQueue({ max_score: 0.6, page: 1, page_size: 10, ...dateRange }),
        getCoachingNotes({ page: 1, page_size: 10, ...dateRange }),
      ]);
      if (fetchId !== fetchIdRef.current) return;
      if (qaRes.success) setQa(qaRes.data);
      else toast.error(qaRes.error || 'Failed to load QA data');
      if (subParamsRes.success) setSubParams(subParamsRes.data?.parameters || []);
      if (compRes.success) setCompliance(compRes.data);
      else toast.error(compRes.error || 'Failed to load compliance data');
      if (rulesRes.success) setComplianceRules(rulesRes.data);
      else toast.error(rulesRes.error || 'Failed to load compliance rules');
      if (reviewRes.success) {
        setReviewQueue(reviewRes.data?.scores || []);
        setReviewTotal(reviewRes.data?.total || 0);
      }
      if (notesRes.success) {
        setCoachingNotes(notesRes.data?.notes || []);
        setNotesTotal(notesRes.data?.total || 0);
      }
    } catch (err) {
      if (fetchId !== fetchIdRef.current) return;
      toast.error('Failed to load coaching data');
    } finally {
      if (fetchId === fetchIdRef.current) setLoading(false);
    }
  }, [debouncedFrom, debouncedTo]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSaveRule = async (formData, ruleId) => {
    const result = ruleId
      ? await updateComplianceRule(ruleId, formData)
      : await createComplianceRule(formData);
    if (result.success) {
      toast.success(ruleId ? 'Rule updated' : 'Rule created');
      setRuleModalOpen(false);
      setSelectedRule(null);
      fetchData();
    } else {
      toast.error(result.error || 'Failed to save rule');
    }
  };

  const handleDeleteRule = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const result = await deleteComplianceRule(deleteTarget.id);
      if (result.success) {
        toast.success('Rule deleted');
        setDeleteTarget(null);
        fetchData();
      } else {
        toast.error(result.error || 'Failed to delete rule');
      }
    } finally {
      setDeleting(false);
    }
  };

  const avgScores = qa?.avg_scores || {};
  const distribution = qa?.quality_distribution || {};
  const catDistribution = qa?.category_distribution || {};
  const totalScored = qa?.total_scored || 0;
  const avgOverall = avgScores.overall || 0;
  const avgTotal100 = qa?.avg_total_score_100;
  const hasCatDistribution = Object.values(catDistribution).some(v => v > 0);
  const hasTotal100 = avgTotal100 !== null && avgTotal100 !== undefined;
  const qualityLabel = hasTotal100
    ? (avgTotal100 >= 90 ? QUALITY_RATINGS.excellent : avgTotal100 >= 80 ? QUALITY_RATINGS.good : avgTotal100 >= 70 ? QUALITY_RATINGS.average : avgTotal100 >= 60 ? QUALITY_RATINGS.below_avg : QUALITY_RATINGS.poor)
    : (avgOverall >= 0.8 ? QUALITY_RATINGS.excellent : avgOverall >= 0.6 ? QUALITY_RATINGS.good : avgOverall >= 0.4 ? QUALITY_RATINGS.needs_work : QUALITY_RATINGS.poor);

  const groupedSubParams = useMemo(() => {
    return subParams.reduce((groups, p) => {
      const cat = p.category || 'Other';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(p);
      return groups;
    }, {});
  }, [subParams]);

  const totalFlagged = compliance?.total_flagged || 0;
  const flagRate = compliance?.flag_rate_pct || 0;
  const bySeverity = compliance?.by_severity || {};
  const topFlags = compliance?.top_flags || [];

  if (loading) return <Spinner />;

  return (
    <div>
      {/* Controls */}
      <div className="flex items-center justify-between mb-6">
        <RefreshButton onClick={fetchData} loading={loading} />
        <DateRangeFilter from={from} to={to} onChange={onChange} />
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <MetricCard label="Total Scored" value={totalScored} color="#6366f1" />
        <MetricCard
          label={hasTotal100 ? 'Avg Score /100' : 'Avg Quality'}
          value={hasTotal100 ? Math.round(avgTotal100) : (avgOverall ? avgOverall.toFixed(2) : '-')}
          color="#22c55e"
        />
        <MetricCard label="Quality Rating" value={qualityLabel} color="#3b82f6" />
        <MetricCard label="Compliance Flags" value={totalFlagged} color="#ef4444" />
        <MetricCard label="Flag Rate" value={flagRate ? `${flagRate.toFixed(1)}%` : '-'} color="#f59e0b" />
      </div>

      {/* Review Queue + Recent Coaching Notes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Review Queue */}
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-700 flex items-center space-x-2">
              <Eye size={16} className="text-red-500" />
              <span>Review Queue</span>
              {reviewTotal > 0 && (
                <span className="text-xs font-normal text-gray-400">({reviewTotal})</span>
              )}
            </h3>
          </div>
          {reviewQueue.length === 0 ? (
            <EmptyState
              title="No items to review"
              description="All interactions are scoring above the threshold."
            />
          ) : (
            <div className="space-y-2">
              <div className="flex items-center justify-between px-3 pb-1 text-[10px] text-gray-400 uppercase tracking-wide">
                <span>Interaction</span>
                <span>Score</span>
              </div>
              {reviewQueue.map((score) => (
                <div
                  key={score.id || score.interaction_id}
                  className="flex items-center justify-between px-3 py-2.5 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/conversations?search=${encodeURIComponent(score.interaction_id)}`)}
                  onKeyDown={onActivateKey(() => navigate(`/conversations?search=${encodeURIComponent(score.interaction_id)}`))}
                  aria-label={`Review interaction ${score.interaction_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-mono text-gray-700 truncate" title={score.interaction_id}>
                        #{score.interaction_id}
                      </span>
                      {score.quality_category && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                          score.quality_category === 'Poor' ? 'bg-red-100 text-red-700'
                          : score.quality_category === 'Below Average' ? 'bg-orange-100 text-orange-700'
                          : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {score.quality_category}
                        </span>
                      )}
                    </div>
                    {score.agent_id && (
                      <p className="text-[11px] text-gray-400 mt-0.5 truncate">Agent: <AgentName id={score.agent_id} /></p>
                    )}
                  </div>
                  <ScorePill score={score.overall_score} />
                </div>
              ))}
            </div>
          )}
          {reviewTotal > reviewQueue.length && (
            <button
              disabled={loadingMore}
              onClick={async () => {
                if (loadingMore) return;
                const nextPage = Math.floor(reviewQueue.length / 10) + 1;
                setLoadingMore(true);
                try {
                  const res = await getReviewQueue({ max_score: 0.6, page: nextPage, page_size: 10, from: debouncedFrom, to: debouncedTo });
                  if (res.success) {
                    setReviewQueue(prev => [...prev, ...(res.data?.scores || [])]);
                  } else {
                    toast.error(res.error || 'Failed to load more items');
                  }
                } catch (err) {
                  toast.error('Failed to load more items');
                } finally {
                  setLoadingMore(false);
                }
              }}
              className="text-xs text-primary-600 hover:text-primary-700 font-medium mt-3 w-full text-center py-1.5 disabled:opacity-50"
            >
              {loadingMore ? 'Loading...' : `Load more (${reviewTotal - reviewQueue.length} remaining)`}
            </button>
          )}
        </div>

        {/* Recent Coaching Notes */}
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-700 flex items-center space-x-2">
              <BookOpen size={16} className="text-primary-500" />
              <span>Recent Coaching Notes</span>
              {notesTotal > 0 && (
                <span className="text-xs font-normal text-gray-400">({notesTotal})</span>
              )}
            </h3>
          </div>
          {coachingNotes.length === 0 ? (
            <EmptyState
              title="No coaching notes"
              description="Notes will appear when coaches review interactions."
            />
          ) : (
            <div className="space-y-2">
              {coachingNotes.map((note) => (
                <div
                  key={note.id}
                  className="px-3 py-2.5 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/conversations?search=${encodeURIComponent(note.interaction_id)}`)}
                  onKeyDown={onActivateKey(() => navigate(`/conversations?search=${encodeURIComponent(note.interaction_id)}`))}
                  aria-label={`View interaction ${note.interaction_id}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-mono text-gray-600 truncate" title={note.interaction_id}>
                        #{note.interaction_id}
                      </span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${STATUS_BADGE[note.status] || STATUS_BADGE.pending}`}>
                        {toTitleCase(note.status)}
                      </span>
                    </div>
                    <span
                      className="text-[10px] text-gray-400"
                      title={note.created_at ? `Note added: ${formatShortDate(note.created_at)}` : undefined}
                    >
                      {formatShortDate(note.occurred_at || note.created_at)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 line-clamp-2">{note.note_text}</p>
                  {note.agent_id && (
                    <p className="text-[11px] text-gray-400 mt-1">Agent: <AgentName id={note.agent_id} /></p>
                  )}
                </div>
              ))}
            </div>
          )}
          {notesTotal > coachingNotes.length && (
            <p className="text-xs text-gray-400 text-center mt-3">
              Showing {coachingNotes.length} of {notesTotal}
            </p>
          )}
        </div>
      </div>

      {/* Two-column layout: Quality Dimensions + Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Quality Dimensions */}
        <div className="bg-white border border-gray-200 rounded-lg p-5" role="region" aria-label="Quality dimensions">
          <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center space-x-2">
            <CheckCircle size={16} className="text-green-500" />
            <span>Quality Dimensions</span>
          </h3>
          {totalScored === 0 ? (
            <EmptyState title="No QA scores yet" description="Scores appear when interactions are analyzed." />
          ) : (
            <div className="space-y-3">
              {Object.entries(QUALITY_LABELS).map(([key, label]) => (
                <DimensionBar key={key} label={label} value={avgScores[key]} />
              ))}
            </div>
          )}
        </div>

        {/* Quality Distribution */}
        <div role="region" aria-label="Quality distribution">
          {totalScored > 0 ? (
            <SentimentPieChart
              positive={hasCatDistribution
                ? ((catDistribution['Excellent'] || 0) + (catDistribution['Good'] || 0))
                : ((distribution.excellent || 0) + (distribution.good || 0))}
              neutral={(hasCatDistribution ? catDistribution['Average'] : distribution.needs_improvement) || 0}
              negative={hasCatDistribution
                ? ((catDistribution['Below Average'] || 0) + (catDistribution['Poor'] || 0))
                : (distribution.poor || 0)}
              labels={QUALITY_DIST_LABELS}
              colors={QUALITY_DIST_COLORS}
              title="Quality Distribution"
            />
          ) : (
            <div className="bg-white border border-gray-200 rounded-lg p-6 flex items-center justify-center h-full">
              <EmptyState title="No distribution data" description="Distribution appears when interactions are scored." />
            </div>
          )}
        </div>
      </div>

      {/* Sub-Parameter Breakdown */}
      {subParams.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-5 mb-6" role="region" aria-label="Sub-parameter breakdown">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-700 flex items-center space-x-2">
              <ListChecks size={16} className="text-indigo-500" />
              <span>Sub-Parameter Breakdown</span>
              <span className="text-xs font-normal text-gray-400">({subParams.length} parameters)</span>
            </h3>
            <div className="flex items-center space-x-3">
              {[
                { color: 'bg-green-500', label: '≥ 80%' },
                { color: 'bg-yellow-500', label: '≥ 60%' },
                { color: 'bg-orange-500', label: '≥ 40%' },
                { color: 'bg-red-500', label: '< 40%' },
              ].map(item => (
                <div key={item.label} className="flex items-center space-x-1">
                  <span className={`w-2 h-2 rounded-full ${item.color}`} aria-hidden="true" />
                  <span className="text-[10px] text-gray-400">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="space-y-4">
            {Object.entries(groupedSubParams).map(([category, params]) => (
              <div key={category}>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">{category}</p>
                <div className="space-y-1.5">
                  {params.map(p => {
                    const pct = p.max_score > 0 ? Math.round((p.avg_score / p.max_score) * 100) : 0;
                    const barColor = pct >= 80 ? 'bg-green-500' : pct >= 60 ? 'bg-yellow-500' : pct >= 40 ? 'bg-orange-500' : 'bg-red-500';
                    return (
                      <div key={p.parameter_id} className="flex items-center space-x-3">
                        <span className="text-xs text-gray-600 w-40 truncate" title={p.name}>{p.name}</span>
                        <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-xs font-medium text-gray-600 w-16 text-right">
                          {p.avg_score.toFixed(1)} / {p.max_score}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Compliance Monitoring */}
      <div className="bg-white border border-gray-200 rounded-lg p-5 mb-6" role="region" aria-label="Compliance monitoring">
        <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center space-x-2">
          <Shield size={16} className="text-amber-500" />
          <span>Compliance Monitoring</span>
        </h3>

        {totalFlagged === 0 ? (
          <div className="flex items-center space-x-2 text-green-600 py-4">
            <CheckCircle size={18} />
            <span className="text-sm">All interactions are compliant. No issues detected.</span>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Severity breakdown */}
            <div className="flex items-center space-x-6">
              {['high', 'medium', 'low'].map(level => (
                <div key={level} className="flex items-center space-x-2">
                  <span className={`w-2.5 h-2.5 rounded-full ${SEVERITY_DOTS[level]}`} aria-hidden="true" />
                  <span className="text-sm text-gray-600 capitalize">{level}:</span>
                  <span className="text-sm font-semibold text-gray-800">{bySeverity[level] || 0}</span>
                </div>
              ))}
            </div>

            {/* Top flags */}
            {topFlags.length > 0 && (
              <div className="space-y-2">
                {topFlags.map((f, i) => (
                  <div
                    key={i}
                    className={`flex items-center justify-between px-3 py-2 rounded-md border ${SEVERITY_COLORS[f.severity] || SEVERITY_COLORS.medium}`}
                  >
                    <div className="flex items-center space-x-2">
                      {f.severity === 'high' ? <XCircle size={14} /> : <AlertTriangle size={14} />}
                      <span className="text-sm font-medium">{f.flag}</span>
                    </div>
                    <span className="text-sm">{f.count} case{f.count !== 1 ? 's' : ''}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Compliance Rules */}
      <div className="bg-white border border-gray-200 rounded-lg p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-700 flex items-center space-x-2">
            <Shield size={16} className="text-primary-500" />
            <span>Compliance Rules</span>
          </h3>
          {/* Compliance rules are compliance_service writes, which require
              analytics:settings (admin only) — NOT analytics:coaching, which a
              manager holds. Gating these on coaching would show a manager
              buttons the API refuses. */}
          <PermissionGate permission={StandardPermission.Analytics.SETTINGS}>
            <button
              onClick={() => { setSelectedRule(null); setRuleModalOpen(true); }}
              className="btn-primary text-xs py-1.5 px-3 flex items-center space-x-1"
            >
              <Plus size={14} />
              <span>Add Rule</span>
            </button>
          </PermissionGate>
        </div>

        {complianceRules.length === 0 ? (
          <EmptyState title="No compliance rules" description="Add rules to monitor interaction compliance." />
        ) : (
          <div className="divide-y divide-gray-100">
            {complianceRules.map((rule) => (
              <div key={rule.id || rule.rule_name} className="py-3 flex items-center justify-between">
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-900">{rule.rule_name}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded capitalize ${SEVERITY_COLORS[rule.severity] || SEVERITY_COLORS.medium}`}>
                      {rule.severity}
                    </span>
                    <span className={`badge ${rule.is_active ? 'badge-success' : 'badge-gray'}`}>
                      {rule.is_active ? 'Active' : 'Disabled'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">{rule.description}</p>
                </div>
                <div className="flex items-center space-x-2">
                  <PermissionGate permission={StandardPermission.Analytics.SETTINGS}>
                    <button onClick={() => { setSelectedRule(rule); setRuleModalOpen(true); }} className="text-gray-400 hover:text-gray-600" aria-label={`Edit rule ${rule.rule_name}`}>
                      <Pencil size={14} />
                    </button>
                  </PermissionGate>
                  <PermissionGate permission={StandardPermission.Analytics.SETTINGS}>
                    <button onClick={() => setDeleteTarget(rule)} className="text-gray-400 hover:text-red-500" aria-label={`Delete rule ${rule.rule_name}`}>
                      <Trash2 size={14} />
                    </button>
                  </PermissionGate>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Compliance Rule Modal */}
      {ruleModalOpen && (
        <ComplianceRuleModal
          isOpen={ruleModalOpen}
          onClose={() => { setRuleModalOpen(false); setSelectedRule(null); }}
          onSave={handleSaveRule}
          rule={selectedRule}
        />
      )}

      {/* Delete Confirm */}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDeleteRule}
        title="Delete Rule"
        message={`Are you sure you want to delete "${deleteTarget?.rule_name}"? This cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        loading={deleting}
      />
    </div>
  );
};

export default CoachingPage;
